import Loading from '@/common/atoms/Loading'
import TextErrors from '@/common/atoms/TextErrors'
import DashboardHomeNote from '@/dashboard/components/DashboardHomeNote'
import DashboardWelcome from '@/dashboard/components/DashboardWelcome'
import { useGovernanceFeed } from '@/log/hooks/useGovernanceFeed'
import NewsList from '@/news/components/NewsList'
import NewsLogList from '@/news/components/NewsLogList'
import NewsTabs, { NewsTab } from '@/news/components/NewsTabs'
import { NewsType, newsTypes } from '@/news/newsTypes'
import { useOrgContext } from '@/org/contexts/OrgContext'
import CircleSearchButton from '@/search/components/CircleSearchButton'
import CircleSearchInput from '@/search/components/CircleSearchInput'
import SearchTypeMenu from '@/search/components/SearchTypeMenu'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  BoxProps,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Stack,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from 'src/icons'
import { useNewsFeed } from '../../news/hooks/useNewsFeed'

export default function DashboardNews(boxProps: BoxProps) {
  const { t } = useTranslation()
  const { org } = useOrgContext()
  const [circleId, setCircleId] = useState<string | undefined>()
  // ACTU-01 (#48): org chart changes get their own tab rather than joining
  // the feed (Yohan, 21/09/2026). The circle filter applies to both.
  const [tab, setTab] = useState<NewsTab>('news')
  const isGovernance = tab === 'governance'

  // Subscribe to news
  // Type filter of the news tab (437c1197). The governance tab lists org chart
  // changes only, so the filter does not apply there (#94, phase 8 places it).
  const [type, setType] = useState<NewsType | undefined>()
  const newsFeed = useNewsFeed(circleId, type)
  const governanceFeed = useGovernanceFeed(circleId, !isGovernance)
  const { news } = newsFeed
  const { error, loading, bottomRef } = isGovernance ? governanceFeed : newsFeed

  return (
    <Stack spacing={0} {...boxProps}>
      {/* Above the home note on purpose: the generic message says WHERE you
          are, the note says what the org wants to tell you. And unlike the
          note, it never hides itself — see udn-03a, D1. */}
      <DashboardWelcome mb={8} />

      <DashboardHomeNote mb={10} />

      <Card boxShadow="none">
        <CardHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <NewsTabs value={tab} onChange={setTab} />

          <ButtonGroup size="sm" variant="outline" spacing={2}>
            {/* The type filter sorts the news feed; the governance tab only
                lists org chart changes, where it means nothing (Yohan, 24/09) */}
            {!isGovernance && (
              <SearchTypeMenu
                types={newsTypes}
                value={type}
                placeholder={t('DashboardNews.type')}
                fontWeight="normal"
                onChange={setType}
              />
            )}

            {circleId ? (
              <CircleSearchInput
                value={circleId}
                defaultPerimeter="myCircles"
                placeholder={t('DashboardNews.circle')}
                maxW="170px"
                onChange={setCircleId}
                onClear={() => setCircleId(undefined)}
              />
            ) : (
              <CircleSearchButton
                defaultPerimeter="myCircles"
                rightIcon={<ChevronDownIcon size="1em" />}
                fontWeight="normal"
                onSelect={setCircleId}
              >
                {t('DashboardNews.circle')}
              </CircleSearchButton>
            )}
          </ButtonGroup>
        </CardHeader>

        {error && (
          <CardBody p={4} pt={0}>
            <TextErrors errors={[error]} />
          </CardBody>
        )}
      </Card>

      {!isGovernance && news?.length === 0 && (
        <Alert status="info" mt={10}>
          <AlertIcon />
          <AlertDescription>
            {t('DashboardNews.empty', { org: org?.name })}
          </AlertDescription>
        </Alert>
      )}

      {!isGovernance && news && <NewsList items={news} showSeparatorTop />}

      {isGovernance && governanceFeed.logs?.length === 0 && (
        <Alert status="info" mt={10}>
          <AlertIcon />
          <AlertDescription>
            {t('DashboardNews.governanceEmpty')}
          </AlertDescription>
        </Alert>
      )}

      {isGovernance && governanceFeed.logs && (
        <NewsLogList logs={governanceFeed.logs} showSeparatorTop />
      )}

      <Box ref={bottomRef} textAlign="center">
        {loading && <Loading active size="sm" mt={10} />}
      </Box>
    </Stack>
  )
}
